import * as CryptoJS from 'crypto-js';

import * as LZString from 'lz-string';

const SECRET_KEY = 'test';

/**
 * Guarda datos en el localStorage con ofuscación
 * @param key Clave para identificar los datos
 * @param value Valor a guardar (puede ser cualquier tipo)
 */
export function saveToStorage(
  key: string,
  value: any,
  storage: 'local' | 'session' = 'local'
): void {
  try {
    // Convertir a string si es un objeto
    
    const valueStr =
      typeof value === 'object' ? JSON.stringify(value) : String(value);

    // Comprimir datos
    const compressed = LZString.compressToEncodedURIComponent(valueStr);
    
    // Cifrar datos comprimidos
    const encrypted = CryptoJS.AES.encrypt(compressed, SECRET_KEY).toString();
    

    // Guardar en localStorage
    storage === 'local'
      ? localStorage.setItem(key, encrypted)
      : sessionStorage.setItem(key, encrypted);
  } catch (error) {
    console.error('Error al guardar datos cifrados:', error);
  }
}

/**
 * Guarda datos en el localStorage con ofuscación de manera asíncrona
 * @param key Clave para identificar los datos
 * @param value Valor a guardar (puede ser cualquier tipo)
 * @param storage Tipo de storage ('local' | 'session')
 * @returns Promise que se resuelve cuando se completa el guardado
 */
export function saveToStorageAsync(
  key: string,
  value: any,
  storage: 'local' | 'session' = 'local'
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      
      // Convertir a string si es un objeto
      const valueStr =
        typeof value === 'object' ? JSON.stringify(value) : String(value);

      // Comprimir datos
      const compressed = LZString.compressToEncodedURIComponent(valueStr);
      
      // Cifrar datos comprimidos
      const encrypted = CryptoJS.AES.encrypt(compressed, SECRET_KEY).toString();
      
      // Usar setTimeout para asegurar que la operación se complete en el siguiente tick
      setTimeout(() => {
        try {
          // Guardar en storage correspondiente
          storage === 'local'
            ? localStorage.setItem(key, encrypted)
            : sessionStorage.setItem(key, encrypted);
          
          // Verificar que el dato se guardó correctamente
          const saved = storage === 'local' 
            ? localStorage.getItem(key) 
            : sessionStorage.getItem(key);
          
          if (saved === encrypted) {
            resolve();
          } else {
            reject(new Error('Failed to verify storage operation'));
          }
        } catch (storageError) {
          reject(storageError);
        }
      }, 0);
    } catch (error) {
      console.error('Error al guardar datos cifrados:', error);
      reject(error);
    }
  });
}

/**
 * Obtiene y descifra datos del localStorage
 * @param key Clave de los datos a recuperar
 * @returns Datos descifrados o null si no existen o hay error
 */
export function getFromStorage<T>(key: string, storage: 'local' | 'session' = 'local'): T | null {
  try {
    // Obtener datos cifrados
    const encrypted = storage === 'local' ? localStorage.getItem(key) : sessionStorage.getItem(key);

    if (!encrypted) {
      return null;
    }

    // Descifrar datos
    const decrypted = CryptoJS.AES.decrypt(encrypted, SECRET_KEY).toString(
      CryptoJS.enc.Utf8
    );

    // Descomprimir datos
    const decompressed = LZString.decompressFromEncodedURIComponent(decrypted);

    if (!decompressed) {
      return null;
    }

    // Convertir a objeto si es posible
    try {
      return JSON.parse(decompressed) as T;
    } catch {
      // Si no es JSON, devolver como string
      return decompressed as unknown as T;
    }
  } catch (error) {
    console.error('Error al recuperar datos cifrados:', error);
    return null;
  }
}

/**
 * Elimina datos del localStorage o sessionStorage
 * @param key Clave de los datos a eliminar
 * @param storage Tipo de storage ('local' | 'session'). Por defecto 'local'.
 */
export function removeFromStorage(key: string, storage: 'local' | 'session' = 'local'): void {
  try {
    storage === 'local' ? localStorage.removeItem(key) : sessionStorage.removeItem(key);
  } catch (error) {
    console.error('Error al eliminar datos del storage:', error);
  }
}

/**
 * Comprueba si existen datos con la clave especificada
 * @param key Clave a comprobar
 * @returns true si existe, false si no
 */
export function existsInStorage(key: string): boolean {
  return localStorage.getItem(key) !== null;
}

/**
 * Limpia todo el localStorage
 */
export function clearStorage(): void {
  localStorage.clear();
}

/**
 * Retorna el sessionId
 */

export const getSessionId = (): number => {
  return Number(getFromStorage('session_id'));
};

/**
 * Guarda datos en storage de manera asíncrona y luego ejecuta una función callback
 * @param key Clave para identificar los datos
 * @param value Valor a guardar
 * @param callback Función a ejecutar después del guardado
 * @param storage Tipo de storage ('local' | 'session')
 * @returns Promise que se resuelve cuando se completan ambas operaciones
 */
export async function saveToStorageAndThen(
  key: string,
  value: any,
  callback: () => void,
  storage: 'local' | 'session' = 'local'
): Promise<void> {
  try {
    await saveToStorageAsync(key, value, storage);
    callback();
  } catch (error) {
    console.error('Error en saveToStorageAndThen:', error);
    // Ejecutamos el callback incluso si falla el storage para no bloquear la aplicación
    callback();
  }
}

/**
 * Función específica para navegar después de guardar
 */
export async function saveToStorageAndNavigate(
  key: string,
  value: any,
  router: any,
  route: string[],
  storage: 'local' | 'session' = 'session'
): Promise<void> {
  return saveToStorageAndThen(
    key,
    value,
    () => router.navigate(route),
    storage
  );
}
